import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useLanguage } from '../context/LanguageContext';
import { ChartLineUp, ChartBar, Clock, CalendarBlank, TrendUp, TrendDown, Minus, Target, MoonStars } from '@phosphor-icons/react';
import InfoHint from './common/InfoHint';

/**
 * DACAMS Part 0 — Data Archaeology panel.
 *
 * Renders the "as-is" picture the dashboard rests on: coverage, the daily traffic
 * series, month/year variation, and the structural patterns (weekly, hourly,
 * seasonality) plus the growth trend. Reads analysis_results.parts.part0_archaeology.
 */

// Aipron chart palette (matches the existing Highcharts components)
const C_BLUE = '#3A7CA5';
const C_ACCENT = '#F26430';
const C_GRID = '#20497B';
const C_TEXT = '#A4B8D3';

interface DayVal { date: string; movements: number; }
interface MonthVal {
  year: number; month: number; label: string; month_name: string;
  movements: number; days_in_data: number; avg_per_day: number;
  pct_change_vs_prev_month: number | null;
}
interface AnnualVal {
  year: number; movements: number; days_in_data: number; avg_per_day: number;
  pct_change_vs_prev_year: number | null;
}
interface Archaeology {
  available: boolean;
  coverage?: {
    date_start: string; date_end: string; days_with_data: number;
    calendar_span_days: number; years_covered: number[]; total_movements: number;
    avg_per_day: number; median_per_day: number;
    peak_day: { date: string; movements: number };
    low_day: { date: string; movements: number };
  };
  daily_series?: DayVal[];
  monthly_series?: MonthVal[];
  annual?: AnnualVal[];
  patterns?: {
    weekly: { dow: number; name: string; avg_movements: number }[];
    weekday_avg: number; weekend_avg: number; weekend_vs_weekday_ratio: number | null;
    hourly_profile: { hour: number; avg_movements: number; share_pct: number }[];
    seasonality_index: { month: number; month_name: string; index: number }[];
  };
  trend?: {
    available: boolean; slope_mov_per_day?: number;
    direction?: string; pct_over_period?: number | null; cagr_pct?: number | null;
  };
  design_days?: {
    available: boolean;
    reason?: string;
    methodology?: string;
    busy_day_by_month?: {
      date: string; total_movements: number; peak_hour: number; peak_hour_movements: number;
      year: number; month: number; month_name: string; days_in_month_data: number;
    }[];
    low_day?: {
      date: string; total_movements: number; peak_hour: number; peak_hour_movements: number;
    } | null;
  };
}

const baseOptions = (): Highcharts.Options => ({
  chart: { backgroundColor: 'transparent', spacing: [8, 8, 8, 8] },
  title: { text: undefined },
  credits: { enabled: false },
  legend: { enabled: false, itemStyle: { color: C_TEXT } },
  xAxis: {
    labels: { style: { color: C_TEXT, fontSize: '10px' } },
    lineColor: C_GRID, gridLineColor: C_GRID, tickColor: C_GRID,
  },
  yAxis: {
    title: { text: undefined },
    labels: { style: { color: C_TEXT, fontSize: '10px' } },
    gridLineColor: C_GRID, min: 0,
  },
  tooltip: {
    backgroundColor: 'rgba(16, 52, 96, 0.92)', borderColor: C_GRID,
    style: { color: '#FFFFFF' },
  },
});

const fmt = (n: number) => Math.round(n).toLocaleString();

const KpiTile = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <div className="pro-card p-4 flex flex-col">
    <p className="kpi-label">{label}</p>
    <p className="kpi-value mt-1" style={{ fontSize: '1.5rem' }}>{value}</p>
    {sub && <p className="text-xs text-[var(--text-tertiary)] mt-1">{sub}</p>}
  </div>
);

const SectionCard = ({ title, icon, hint, children }: { title: string; icon: React.ReactNode; hint?: React.ReactNode; children: React.ReactNode }) => (
  <div className="pro-card p-5">
    <div className="flex items-center gap-2 mb-3">
      <span className="text-[var(--aipron-accent)]">{icon}</span>
      <h4 className="text-sm font-bold font-display text-[var(--text-primary)]">{title}</h4>
      {hint && <InfoHint text={hint} />}
    </div>
    {children}
  </div>
);

const DataArchaeologyPanel = ({ project }: { project: any }) => {
  const { language } = useLanguage();
  const isEs = (language as string) === 'es';
  const arch: Archaeology | undefined = project?.analysis_results?.parts?.part0_archaeology;

  if (!arch || !arch.available || !arch.coverage) return null;

  const cov = arch.coverage;
  const t = arch.trend;
  const daily = arch.daily_series || [];
  const annual = arch.annual || [];
  const patterns = arch.patterns;
  const designDays = arch.design_days;
  const busyDays = designDays?.busy_day_by_month || [];

  const trendIcon = t?.direction === 'increasing'
    ? <TrendUp size={14} weight="bold" />
    : t?.direction === 'decreasing' ? <TrendDown size={14} weight="bold" /> : <Minus size={14} />;
  const trendPct = t?.available && t.pct_over_period != null
    ? `${t.pct_over_period > 0 ? '+' : ''}${t.pct_over_period}%` : '—';

  // Daily traffic series (area)
  const dailyOptions: Highcharts.Options = {
    ...baseOptions(),
    chart: { ...baseOptions().chart, type: 'areaspline', height: 220 },
    xAxis: { ...baseOptions().xAxis, categories: daily.map(d => d.date),
      tickInterval: Math.max(1, Math.floor(daily.length / 8)),
      labels: { style: { color: C_TEXT, fontSize: '9px' }, rotation: -45 } },
    series: [{
      type: 'areaspline', name: isEs ? 'Movimientos/día' : 'Movements/day',
      data: daily.map(d => d.movements), color: C_BLUE,
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [[0, 'rgba(58,124,165,0.45)'], [1, 'rgba(58,124,165,0.02)']],
      },
      lineWidth: 2, marker: { enabled: false },
    }],
  };

  // Weekly profile (column)
  const weeklyOptions: Highcharts.Options = {
    ...baseOptions(),
    chart: { ...baseOptions().chart, type: 'column', height: 200 },
    xAxis: { ...baseOptions().xAxis, categories: (patterns?.weekly || []).map(w => w.name) },
    series: [{
      type: 'column', name: isEs ? 'Media' : 'Avg',
      data: (patterns?.weekly || []).map((w, i) => ({ y: Math.round(w.avg_movements), color: i >= 5 ? C_ACCENT : C_BLUE })),
    }],
    plotOptions: { column: { borderRadius: 3 } },
  };

  // Hourly profile (area)
  const hourlyOptions: Highcharts.Options = {
    ...baseOptions(),
    chart: { ...baseOptions().chart, type: 'areaspline', height: 200 },
    xAxis: { ...baseOptions().xAxis, categories: (patterns?.hourly_profile || []).map(h => `${h.hour}h`) },
    series: [{
      type: 'areaspline', name: isEs ? 'Media/hora' : 'Avg/hour',
      data: (patterns?.hourly_profile || []).map(h => Math.round(h.avg_movements * 100) / 100),
      color: C_ACCENT, fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [[0, 'rgba(242,100,48,0.40)'], [1, 'rgba(242,100,48,0.02)']],
      }, lineWidth: 2, marker: { enabled: false },
    }],
  };

  // Annual comparison (multi-year) OR seasonality (single year)
  const multiYear = annual.length >= 2;
  const annualOptions: Highcharts.Options = {
    ...baseOptions(),
    chart: { ...baseOptions().chart, type: 'column', height: 200 },
    xAxis: { ...baseOptions().xAxis, categories: annual.map(a => String(a.year)) },
    series: [{
      type: 'column', name: isEs ? 'Mov./año' : 'Mov./year',
      data: annual.map(a => a.movements), color: C_BLUE,
      dataLabels: {
        enabled: true, style: { color: C_TEXT, textOutline: 'none', fontSize: '9px' },
        formatter: function () { return fmt(this.y || 0); },
      },
    }],
    plotOptions: { column: { borderRadius: 3 } },
  };
  const seasonOptions: Highcharts.Options = {
    ...baseOptions(),
    chart: { ...baseOptions().chart, type: 'line', height: 200 },
    xAxis: { ...baseOptions().xAxis, categories: (patterns?.seasonality_index || []).map(s => s.month_name) },
    yAxis: { ...baseOptions().yAxis, min: null as any, plotLines: [{ value: 1, color: C_ACCENT, width: 1, dashStyle: 'Dash' }] },
    series: [{
      type: 'line', name: isEs ? 'Índice' : 'Index',
      data: (patterns?.seasonality_index || []).map(s => s.index), color: C_ACCENT,
      lineWidth: 2, marker: { radius: 3 },
    }],
  };

  // Busy day by month (design days) — peak-hour movements per month's design day
  const busyDayOptions: Highcharts.Options = {
    ...baseOptions(),
    chart: { ...baseOptions().chart, type: 'column', height: 220 },
    xAxis: { ...baseOptions().xAxis, categories: busyDays.map(d => d.month_name) },
    yAxis: { ...baseOptions().yAxis, title: { text: isEs ? 'Mov./hora pico' : 'Peak-hour mov.', style: { color: C_TEXT, fontSize: '10px' } } },
    tooltip: {
      ...baseOptions().tooltip,
      formatter: function () {
        const d = busyDays[this.point.index];
        if (!d) return '';
        return isEs
          ? `<b>${d.month_name}</b><br/>${isEs ? 'Fecha' : 'Date'}: ${d.date}<br/>${isEs ? 'Total día' : 'Day total'}: ${fmt(d.total_movements)}<br/>${isEs ? 'Hora pico' : 'Peak hour'}: ${d.peak_hour}h<br/>${isEs ? 'Mov. en hora pico' : 'Peak-hour mov.'}: <b>${fmt(d.peak_hour_movements)}</b>`
          : `<b>${d.month_name}</b><br/>Date: ${d.date}<br/>Day total: ${fmt(d.total_movements)}<br/>Peak hour: ${d.peak_hour}h<br/>Peak-hour mov.: <b>${fmt(d.peak_hour_movements)}</b>`;
      },
    },
    series: [{
      type: 'column', name: isEs ? 'Mov. hora pico' : 'Peak-hour mov.',
      data: busyDays.map(d => d.peak_hour_movements), color: C_ACCENT,
    }],
    plotOptions: { column: { borderRadius: 3 } },
  };

  return (
    <div className="pro-card p-5" style={{ borderLeft: '3px solid var(--aipron-accent)' }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="eyebrow flex items-center gap-1.5">
            {isEs ? 'Arqueología de datos' : 'Data Archaeology'}
            <InfoHint
              align="left"
              text={isEs
                ? 'Reconstruye la operativa real del aeropuerto a partir del histórico de vuelos (AODB): cuánto tráfico hay, cómo se reparte por día, hora y mes, y su tendencia. Es la foto "As-Is" sobre la que se calcula todo lo demás.'
                : 'Reconstructs the airport\'s real operation from the historical flight log (AODB): how much traffic there is, how it spreads across day, hour and month, and its trend. It is the "As-Is" picture everything else is computed on.'}
            />
          </p>
          <h3 className="text-lg font-bold font-display text-[var(--text-primary)] mt-0.5">
            {isEs ? 'Foto As-Is del aeropuerto' : 'Airport As-Is picture'}
          </h3>
        </div>
        <span className="badge badge-neutral">
          {cov.date_start} → {cov.date_end}
        </span>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <KpiTile label={isEs ? 'Movimientos' : 'Movements'} value={fmt(cov.total_movements)}
          sub={`${cov.days_with_data} ${isEs ? 'días' : 'days'}`} />
        <KpiTile label={isEs ? 'Media/día' : 'Avg/day'} value={fmt(cov.avg_per_day)}
          sub={`${isEs ? 'mediana' : 'median'} ${fmt(cov.median_per_day)}`} />
        <KpiTile label={isEs ? 'Día pico' : 'Peak day'} value={fmt(cov.peak_day.movements)}
          sub={cov.peak_day.date} />
        <KpiTile label={isEs ? 'Día valle' : 'Low day'} value={fmt(cov.low_day.movements)}
          sub={cov.low_day.date} />
        <div className="pro-card p-4 flex flex-col">
          <p className="kpi-label">{isEs ? 'Tendencia' : 'Trend'}</p>
          <p className="kpi-value mt-1 flex items-center gap-1" style={{ fontSize: '1.5rem' }}>
            {trendIcon}{trendPct}
          </p>
          <p className="text-xs text-[var(--text-tertiary)] mt-1">
            {t?.cagr_pct != null ? `CAGR ${t.cagr_pct > 0 ? '+' : ''}${t.cagr_pct}%` : (isEs ? 'periodo' : 'over period')}
          </p>
        </div>
      </div>

      {/* Daily series */}
      <div className="mb-4">
        <SectionCard title={isEs ? 'Tráfico diario' : 'Daily traffic'} icon={<ChartLineUp size={16} />}
          hint={isEs
            ? 'Movimientos (llegadas + salidas) por día en todo el periodo con datos. Sirve para ver picos, valles y huecos de datos.'
            : 'Movements (arrivals + departures) per day across the whole data period. Shows peaks, troughs and data gaps.'}>
          <HighchartsReact highcharts={Highcharts} options={dailyOptions} />
        </SectionCard>
      </div>

      {/* Patterns grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <SectionCard title={isEs ? 'Perfil semanal' : 'Weekly profile'} icon={<CalendarBlank size={16} />}
          hint={isEs
            ? 'Media de movimientos por día de la semana. Muestra si el fin de semana baja o iguala al laborable.'
            : 'Average movements by day of week. Shows whether weekends drop below or match weekdays.'}>
          <HighchartsReact highcharts={Highcharts} options={weeklyOptions} />
          {patterns && (
            <p className="text-xs text-[var(--text-tertiary)] mt-2">
              {isEs ? 'Laborable' : 'Weekday'} {fmt(patterns.weekday_avg)} · {isEs ? 'Finde' : 'Weekend'} {fmt(patterns.weekend_avg)}
              {patterns.weekend_vs_weekday_ratio != null && ` (${Math.round(patterns.weekend_vs_weekday_ratio * 100)}%)`}
            </p>
          )}
        </SectionCard>
        <SectionCard title={isEs ? 'Perfil horario típico' : 'Typical hourly profile'} icon={<Clock size={16} />}
          hint={isEs
            ? 'Reparto medio del tráfico a lo largo del día (24 h). Señala en qué horas se concentran las puntas.'
            : 'Average spread of traffic across the day (24 h). Points to when the peaks concentrate.'}>
          <HighchartsReact highcharts={Highcharts} options={hourlyOptions} />
        </SectionCard>
      </div>

      {/* Annual (multi-year) or seasonality (single year) */}
      <SectionCard
        title={multiYear ? (isEs ? 'Comparativa anual' : 'Annual comparison') : (isEs ? 'Estacionalidad (índice mensual)' : 'Seasonality (monthly index)')}
        icon={<ChartBar size={16} />}
        hint={multiYear
          ? (isEs ? 'Movimientos totales por año, con el cambio porcentual respecto al año anterior.' : 'Total movements per year, with the percentage change versus the previous year.')
          : (isEs ? 'Índice de tráfico por mes: 1 = media anual. Por encima de 1 el mes está más cargado que la media; por debajo, más flojo.' : 'Traffic index by month: 1 = annual mean. Above 1 the month is busier than average; below, quieter.')}>
        <HighchartsReact highcharts={Highcharts} options={multiYear ? annualOptions : seasonOptions} />
        {multiYear && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-[var(--text-tertiary)]">
            {annual.filter(a => a.pct_change_vs_prev_year != null).map(a => (
              <span key={a.year}>
                {a.year}: <span className={a.pct_change_vs_prev_year! >= 0 ? 'text-[var(--aipron-success)]' : 'text-[var(--aipron-danger)]'}>
                  {a.pct_change_vs_prev_year! > 0 ? '+' : ''}{a.pct_change_vs_prev_year}%
                </span>
              </span>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Design days */}
      {designDays?.available && busyDays.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          <div className="lg:col-span-2">
            <SectionCard title={isEs ? 'Días de diseño (busy day/mes)' : 'Design days (busy day/month)'} icon={<Target size={16} />}
              hint={isEs
                ? 'El día más representativo de cada mes: el 2º más cargado de su semana punta (criterio IATA). Su "mov. hora pico" es la demanda punta de ese mes (una hora de reloj completa) y alimenta la curva de saturación estacional. OJO: es un día distinto en cada mes, por eso su pico difiere del pico del busy day anual que se ve en otras tarjetas.'
                : 'The most representative day of each month: the 2nd busiest of its peak week (IATA criterion). Its "peak-hour mov." is that month\'s peak demand (a full clock hour) and feeds the seasonal saturation curve. NOTE: it is a different day each month, so its peak differs from the annual busy-day peak shown on other cards.'}>
              <HighchartsReact highcharts={Highcharts} options={busyDayOptions} />
              <div className="overflow-x-auto mt-3">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{isEs ? 'Mes' : 'Month'}</th>
                      <th>{isEs ? 'Fecha' : 'Date'}</th>
                      <th>{isEs ? 'Total día' : 'Day total'}</th>
                      <th>{isEs ? 'Hora pico' : 'Peak hour'}</th>
                      <th>{isEs ? 'Mov. hora pico' : 'Peak-hour mov.'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {busyDays.map(d => (
                      <tr key={d.date}>
                        <td className="font-sans">{d.month_name} {d.year}</td>
                        <td>{d.date}</td>
                        <td>{fmt(d.total_movements)}</td>
                        <td>{d.peak_hour}h</td>
                        <td className="text-[var(--aipron-accent)] font-semibold">{fmt(d.peak_hour_movements)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {designDays.methodology && (
                <p className="text-xs text-[var(--text-tertiary)] mt-2">{designDays.methodology}</p>
              )}
            </SectionCard>
          </div>

          <SectionCard title={isEs ? 'Día valle representativo' : 'Representative low day'} icon={<MoonStars size={16} />}
            hint={isEs
              ? 'Un día tranquilo representativo del año (no un mínimo puntual). Marca el suelo del rango de demanda frente a los días punta.'
              : 'A representative quiet day of the year (not a one-off minimum). It marks the floor of the demand range against the peak days.'}>
            {designDays.low_day ? (
              <div className="flex flex-col gap-3">
                <div>
                  <p className="kpi-label">{isEs ? 'Fecha' : 'Date'}</p>
                  <p className="kpi-value mt-1" style={{ fontSize: '1.25rem' }}>{designDays.low_day.date}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="kpi-label">{isEs ? 'Total día' : 'Day total'}</p>
                    <p className="kpi-value mt-1" style={{ fontSize: '1.1rem' }}>{fmt(designDays.low_day.total_movements)}</p>
                  </div>
                  <div>
                    <p className="kpi-label">{isEs ? 'Hora pico' : 'Peak hour'}</p>
                    <p className="kpi-value mt-1" style={{ fontSize: '1.1rem' }}>{designDays.low_day.peak_hour}h</p>
                  </div>
                </div>
                <div>
                  <p className="kpi-label">{isEs ? 'Mov. en hora pico' : 'Peak-hour mov.'}</p>
                  <p className="kpi-value mt-1" style={{ fontSize: '1.1rem' }}>{fmt(designDays.low_day.peak_hour_movements)}</p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-[var(--text-tertiary)]">
                {isEs ? 'Sin día valle representativo en los datos disponibles.' : 'No representative low day in the available data.'}
              </p>
            )}
          </SectionCard>
        </div>
      )}
      {designDays && !designDays.available && (
        <p className="text-xs text-[var(--text-tertiary)] mt-4">
          {isEs ? 'Días de diseño no disponibles' : 'Design days unavailable'}
          {designDays.reason ? `: ${designDays.reason}` : ''}
        </p>
      )}
    </div>
  );
};

export default DataArchaeologyPanel;
